/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import {initFBShowcaseWdgts as loadFBQuestionsAndShowcase} from './methods/fetchEntityData.js'
import changeCurrentLocation from './methods/changeCurrentLocation.js'
import Vue from 'vue'
import Router from 'vue-router'
import { auth } from './firebase/firebaseConfig.js'
import store from './store/store'
import SurveyFlow22 from './views/SurveyFlow22/SurveyFlow22.vue'
import TextInvites from './views/TextInvites24/TextInvites.vue'
import EmailInvites from './views/EmailInvites23/EmailInvites.vue'
import Allfeedback from './views/Allfeedback.vue'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [
    {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/',
          redirect: '/dash/home'
        },
        {
          path: '/dash/home',
          name: 'home',
          component: () => import('./views/Home.vue'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Home'
          }
        },
        {
          path: '/dash/responses',
          name: 'responses',
          component: Allfeedback,
          meta: {
            rule: 'admin',
            pageTitle: 'View Feedback & Reviews',
            authRequired: true
          }
        },
        {
          path: '/dash/showcase/:installwidgetinstructions?',
          name: 'showcase',
          component: () => import('./views/Showcase22/Showcase22.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Showcase Widgets',
            authRequired: true
          }
        },
        {
          path: '/dash/showcase/setupwidget/:widgettypeorid',
          name: 'showcase-setup-widget',
          component: () => import('./views/Showcase22/CreateEditShowcaseWidget.vue'),
          meta: {
            parent: 'showcase',
            rule: 'admin',
            pageTitle: 'Widget Setup',
            authRequired: true
          }
        },
        {
          path: '/dash/referrals',
          name: 'referrals',
          component: () => import('./views/Referrals22/ReferralsView.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Referrals',
            authRequired: true
          }
        },
        {
          //"Add a referral by hand" moved to a tab on /dash/referrals (June 2026) —
          //keep the old Collect-menu URL alive for bookmarks
          path: '/dash/getfeedback/referrals',
          redirect: '/dash/referrals'
        },
        {
          path: '/dash/settings/referrals',
          name: 'settings-referrals',
          component: () => import('./views/Referrals22/ReferralsSettings.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Referral Program',
            authRequired: true
          }
        },
        {
          path: '/dash/responses/:questionid',
          name: 'responses',
          component: () => import('./views/Allfeedback.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'View Feedback & Reviews',
            authRequired: true
          }
        },
        {
          path: '/dash/surveyflow',
          name: 'survey-flow',
          component: SurveyFlow22,
          meta: {
            rule: 'admin',
            pageTitle: 'Survey Flow',
            authRequired: true
          }
        },
        {
          path: '/dash/surveyflow/externallinks',
          name: 'survey-flow-externallinks',
          component: () => import('./views/Externallinks.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'External Links',
            authRequired: true,
            parent: 'survey-flow',
            /*breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Survey Flow', url:'/dash/surveyflow'},
              { title: 'External Links', active: true }
            ]*/
          }
        },
        {
          path: '/dash/surveyflow/addquestion',
          name: 'survey-flow-add',
          component: () => import('./views/Addquestion.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Add Question',
            authRequired: true,
            parent: 'survey-flow',
            /*breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Survey Flow', url:'/dash/surveyflow'},
              { title: 'Add question', active: true }
            ]*/
          }
        },
        {
          path: '/dash/surveyflow/editquestion/:questionid',
          name: 'survey-flow-edit',
          component: () => import('./views/Editquestion.vue'),
          meta: {
            rule: 'admin',
            parent: 'survey-flow',
            pageTitle: 'Edit Question',
            authRequired: true,
            /*breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Survey Flow', url:'/dash/surveyflow'},
              { title: 'Edit question', active: true }
            ],*/
          }
        },
        {
          path: '/dash/getfeedback/qrcodelink',
          name: 'getfeedback-qrcodelink',
          component: () => import('./views/QRCodeLink.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Link & QR Code',
            authRequired: true
          }
        },
        {
          path: '/dash/getfeedback/socialmedia',
          name: 'getfeedback-socialmedia',
          component: () => import('./views/Socialmedia.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Get feedback on social media',
            authRequired: true
          }
        },
        {
          path: '/dash/getfeedback/sendinvites/email',
          name: 'getfeedback-sendinvites-email',
          component: EmailInvites,
          meta: {
            rule: 'admin',
            /*breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Send Invites', url:'/dash/getfeedback/sendinvites'},
              { title: 'Email Invites', active: true }
            ],*/
            pageTitle: 'Email Invites',
            parent: 'getfeedback-sendinvites',
            authRequired: true
          }
        },
        {
          path: '/dash/getfeedback/sendinvites/text',
          name: 'getfeedback-sendinvites-text',
          component: TextInvites,
          meta: {
            rule: 'admin',
            /*breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Send Invites', url:'/dash/getfeedback/sendinvites'},
              { title: 'Text Invites', active: true }
            ],*/
            pageTitle: 'Text/SMS Invites',
            parent: 'getfeedback-sendinvites',
            authRequired: true
          }
        },
        {
          path: '/dash/getfeedback/automate/developers',
          name: 'getfeedback-automate-developers',
          component: () => import('./views/DeveloperAPI.vue'),
          meta: {
            rule: 'admin',
            /*breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Automate', url:'/dash/getfeedback/automate'},
              { title: 'Developer API', active: true }
            ],*/
            pageTitle: 'Automate using developer API',
            parent: 'getfeedback-automate',
            authRequired: true
          }
        },
        {
          path: '/dash/getfeedback/emailsignature',
          name: 'getfeedback-emailsignature',
          component: () => import('./views/Emailsignature.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Email signature',
            authRequired: true
          }
        },
        {
          path: '/dash/getfeedback/website',
          name: 'getfeedback-website',
          component: () => import('./views/Website.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Website feedback',
            authRequired: true
          }
        },
        {
          path: '/dash/getfeedback/automate',
          name: 'getfeedback-automate',
          component: () => import('./views/Automation.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Automate',
            authRequired: true
          }
        },
        {
          path: '/dash/settings',
          name: 'settings',
          component: () => import('./views/Settings.vue'),
          meta: {
            rule: 'admin',
            isGlobal: true,
            pageTitle: 'Settings',
            authRequired: true
          }
        },
        {
          path: '/dash/settings/businessinformation',
          name: 'settings-businessinformation',
          component: () => import('./views/Businessinformation.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Business Details',
            authRequired: true
          }
        },
        {
          path: '/dash/settings/lookandfeel',
          name: 'settings-lookandfeel',
          component: () => import('./views/Lookandfeel.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Look and Feel',
            authRequired: true,
          },
        },
        {
          path: '/dash/settings/notifications',
          name: 'settings-notifications',
          component: () => import('./views/Notificationsettings.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Notifications',
            authRequired: true,
          },
        },
        {
          path: '/dash/settings/sending',
          name: 'settings-sending',
          component: () => import('./views/SendingSettings.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Email & Text Sending',
            authRequired: true,
          },
        },
        {
          path: '/dash/settings/language',
          name: 'settings-language',
          component: () => import('./views/Language.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Language',
          },
        },
        {
          path: '/dash/settings/sharingandreviews',
          name: 'settings-sharingandreviews',
          component: () => import('./views/Sharingandreviews.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Social Platforms',
            authRequired: true
          }
        },
        {
          path: '/dash/settings/collaborators',
          name: 'settings-collaborators',
          component: () => import('./views/Collaborators.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Collaborators',
            authRequired: true,
          }
        },
        {
          path: '/dash/settings/myprofile',
          name: 'settings-myprofile',
          component: () => import('./views/Myprofile.vue'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'My profile'
          }
        },
        {
          path: '/dash/settings/plan',
          name: 'settings-plan',
          //2026 pricing: Checkout/Portal-based plan page. The old
          //Plan__WithAgencySplit.vue + Subscribe views are deleted at launch cleanup.
          component: () => import('./views/Plan.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Plan',
            authRequired: true,
            parent: 'settings',
            meta: {
              isGlobal: true
            },
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url:'/dash/settings'},
              { title: 'Plan', active: true }
            ]
          }
        },
        {
          path: '/dash/settings/customdomain',
          name: 'custom-domain',
          component: () => import('@/views/CustomDomain.vue'),
          meta: {
            pageTitle: 'Custom domain',
            rule: 'editor',
            authRequired: true
          }
        },
        {
          path: '/dash/settings/locations',
          name: 'settings-locations',
          component: () => import('./views/Locations.vue'),
          meta: {
            rule: 'admin',
            pageTitle: 'Spaces',
            parent: 'settings',
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url:'/dash/settings'},
              { title: 'Spaces', active: true }
            ]
          },

        }
        // Routes 'settings-plan-subscribe' (pre-Checkout Stripe Elements subscribe)
        // and 'settings-addon-subscribe' (video add-on checkout) removed at 2026
        // pricing launch cleanup: billing now goes through Stripe Checkout/Portal
        // from Plan.vue, and video is included in every plan. Their component files
        // (Subscribe__WithAgencySplit.vue, Subscribe_Addon_WithAgencySplit.vue) are deleted.

      ]
    },
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/dash/login',
          name: 'login',
          component: () => import('@/views/loggedoutpages/login/Login.vue'),
          meta: {
            authRequired: false,
            rule: 'editor'
          }
        },
        {
          path: '/dash/signup/:provider',
          name: 'signup',
          component: () => import('@/views/loggedoutpages/register/RegisterLTD.vue'),
          meta: {
            authRequired: false,
            rule: 'editor'
          }
        },
        {
          path: '/dash/forgotpassword',
          name: 'forgotpassword',
          component: () => import('@/views/loggedoutpages/ForgotPassword.vue'),
          meta: {
            authRequired: false,
            rule: 'editor'
          }
        }
      ]
    },
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPageSetup.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/dash/signup',
          name: 'signup',
          component: () => import('@/views/setup/Register.vue'),
          meta: {
            authRequired: false,
            rule: 'editor'
          }
        },
        {
          path: '/dash/signup-invited',
          name: 'signup-invited',
          component: () => import('@/views/setup/RegisterInvite.vue'),
          meta: {
            authRequired: false,
            rule: 'editor'
          }
        },        
        {
          path: '/dash/setup',
          name: 'setup',
          component: () => import('@/views/setup/Setupmain.vue'),
          meta: {
            rule: 'editor',
            authRequired: true
          }
        },
        {
          path: '/dash/addnew', //ADD NEW LOCATION
          name: 'addnew', //ADD NEW LOCATION
          component: () => import('@/views/setup/Setupmain.vue'),
          meta: {
            rule: 'editor',
            authRequired: true
          }
        },
        {
          path: '/dash/addclient', //ADD NEW AGENCY CLIENT
          name: 'addnewclient', //ADD NEW AGENCY CLIENT
          component: () => import('@/views/setup/Setupmain.vue'),
          meta: {
            rule: 'editor',
            authRequired: true
          }
        }
      ]
    },

    /*
    {
      path: '',
      component: () => import('@/layouts_fbl/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/setup',
          name: 'setup',
          component: () => import('@/views_fbl/setup/Setupmain.vue'),
          meta: {
            rule: 'editor',
            authRequired: true
          }
        }
      ]
    },*/
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/dash/home'
    }
  ]
})



router.beforeEach((to, from, next) => {

    //noconsoleconsole.log('inside gate')

    const firebaseCurrentUser = auth.currentUser


    if(!firebaseCurrentUser) {
      //noconsoleconsole.log('no user')
      if (to.meta.authRequired) {
          //noconsoleconsole.log('auth required')
          next({ path: '/dash/login', query: { to: to.fullPath } })
      } else {
        //noconsoleconsole.log('auth not required')
        return next()
      }
    } else {
      //noconsoleconsole.log('has user')
       if (
           to.path === "/dash/login" ||
           to.path === "/dash/forgotpassword" ||
           to.path === "/dash/signup/appsumo" ||
           to.path === "/dash/signup/pitchground" ||
           to.path === "/dash/signup/dealify" ||
           to.path === "/dash/signup-invited" ||
           to.path === "/dash/signup"
      ) {
        //console.log('l i u g t w p')
        return next('/dash/home?logoutfirst=true')
      }
    }

    //USER GOING TO EITHER SETUP OR PANEL
    //check  trial or cancelled
    //console.log(store.getters['dash/subscription'].isTrialEnded)
    let cancelled = false
    if (typeof store.state.dash.viewData.business.cancelled !== "undefined") {
        if (store.state.dash.viewData.business.cancelled) {
          cancelled = true
        }
    }

    if (store.getters['dash/subscription'].isTrialEnded === true || cancelled) {
      if (to.name === 'settings-plan' || to.name === 'settings-plan-subscribe' || to.name === 'settings-notifications' || to.name === 'addnewclient') {
        //I still need to be able to subscribe or update my notifications
      } else {
        next('/dash/settings/plan')
        return
      }
    }

    if (to.name === 'setup' || to.name === 'addnew' || to.name === 'addnewclient') {    
      
      // CRUCIAL: DOUBLE CHECK THAT USER HAS NO ENTITY
      // IF HE/SHE HAS, REDIRECT TO /dash/HOME with retun

      //QUESTION - HOW DOES THIS RELATE WITH AN AGENCY/COLLABORATOR ETC.?

      //load language packs for use in setup
        if (!store.state.dash.viewState.officalLanguagesLoaded) {
          store.dispatch('dash/LOAD_OFFICIAL_LANGUAGES').then(() => {})
        }        

    } else {

      if (store.state.dash.validLoggedInLoaded && store.state.dash.allEntitiesLoaded) {
        //noconsoleconsole.log('is valid logged in, going to __' + to.path +'__')


      } else {
        //noconsole.log('setting up panel')

        if (to.name === 'addnew') {
          window.location.href = 'https://feedback.link/dash/home'
          return
        }

        //is this an agency's client?
        let agencyC = false
        if (typeof to.query.client !== "undefined") {
          agencyC = to.query.client
        }

        //is this a request to view a specific entity?
        let viewBusiness = false
        if (typeof to.query.e !== "undefined") {
          viewBusiness = to.query.e
        }

        store.dispatch('dash/LOGIN_GET_ACCESS', {
          user: firebaseCurrentUser,
          viewBusiness,
          agencyC
        }).then((hasAccess) => {

          if (hasAccess === false ) {
            
            //VERY IMPORTANT, THIS TELLS US IF THIS CUSTOMER HAS ALREADY COMPLETED SETUP OR NOT
            next('/dash/setup')
            return

          } else {
            
            //this actually more like 'GET_ALL_ENTITIES'
            store.dispatch('dash/LOAD_OFFICIAL_LANGUAGES').then(() => {
              store.dispatch('dash/GET_ENTITY', {
                viewBusiness //do we want to load a specific entity first??
              }).then(() => {                                
                
                //FOR EXTERNAL ENTRY FROM EMAIL - THIS ALLOWS A SPECIFIC BUSINESS TO BE OPENED
                /*if (typeof to.query.viewbusiness !== 'undefined') {
                  if (store.state.dash.isMulti) {
                    let busID = String(to.query.viewbusiness).trim()
                    let index = 0
                    if (busID === 'new') {
                      index = store.state.dash.multiLocations.length - 1
                    } else {
                      store.state.dash.multiLocations.forEach((ml,i) => {
                        if (ml.businessID === busID) {
                          index = i
                        }
                      })
                    }
                    changeCurrentLocation(index)
                  }
                }*/
                
                
                /*
                //check for agency branding of current entity and user
                const queryString = window.location.search;
                const urlParams = new URLSearchParams(queryString);

                let brandHex = false
                let brandLogo = false
                if ( urlParams.get('bh') !== null ) {
                  if ( urlParams.get('blu') !== null ) {
                    brandHex = decodeURIComponent(urlParams.get('bh'))
                    brandLogo = decodeURI(urlParams.get('blu'))
                  }
                }          

                if (store.state.dash.loggedInUser.branding && !brandHex && !brandLogo) {
                  //if there is branding in url, not needed to check the uid doc, as app.vue will load the theme
                  //noconsoleconsole.log('branding:')
                  //noconsoleconsole.log(store.state.dash.loggedInUser.branding)
                  if (typeof store.state.dash.loggedInUser.branding[store.state.dash.viewData.business.businessID] !== 'undefined') {
                    //noconsoleconsole.log(store.state.dash.loggedInUser.branding[store.state.dash.viewData.business.businessID])
                    let branding = store.state.dash.loggedInUser.branding[store.state.dash.viewData.business.businessID]
                    brandHex = branding.brandHex
                    brandLogo = branding.logoURL
                        //console.log(urlParams.get('bh'))
                        //brandLogo = 'https://via.placeholder.com/260x60'
                        store.commit('dash/CHANGE_BRAND', {
                          brandHex: brandHex,
                          //CORS BLOCK
                          //brandLogo: decodeURIComponent(urlParams.get('blu'))
                          //CORS BLOCK
                          brandLogo: brandLogo
                        }
                      )
                  }

                  //ADD IT TO THE URL
                    let params = {
                      bh: brandHex ? brandHex : store.state.dash.brandHex,
                      blu: brandLogo? brandLogo :store.state.dash.logoURL
                    }
                    history.pushState(
                      {},
                      null,
                      to.path +
                        '?' +
                        Object.keys(params)
                          .map(key => {
                            return (
                              encodeURIComponent(key) + '=' + encodeURIComponent(params[key])
                            )
                          })
                          .join('&')
                    )
                }*/

                
                /*//GET NPS FOR ONE OR MANY BUSINESSES:
                if (store.state.dash.isMulti) {
                  store.state.dash.multiLocations.forEach((ml,i) => {
                    store.dispatch('dash/GET_ANALYTICS', {
                      bID: ml.businessID,
                      target: i
                    })
                  })
                } else {
                  store.dispatch('dash/GET_ANALYTICS', {
                    bID: store.state.dash.viewData.business.businessID,
                    target: 0
                  })
                }*/

                store.dispatch('dash/GET_ANALYTICS', {
                  bID: store.state.dash.viewData.business.businessID,
                  target: 0
                })

                //check for new reviews
                store.dispatch('dash/I_HAVE_LOGGED_IN')
              
                //noconsoleconsole.log('SETTING UP LAST ACCESSED IN 90s')
                setTimeout(() => {
                  store.dispatch('dash/SET_LAST_ACCESSED', firebaseCurrentUser)
                }, 90000)                

                loadFBQuestionsAndShowcase()

              })
            })

          }

        }).catch((error) => {

          // NO PERMISSION TO ANY BUSINESS, OR ERROR
          console.log('ERROR IN LOGIN, LOADING FEEDBACK, QUESTIONS OR LISTS')
          console.log(error)
          next()

        })
      }
    }

    //WE NOW HAVE A LEGIT USER INSIDE THE DASH

    //console.log('WE NOW HAVE A LEGIT USER INSIDE THE DASH')
    //noconsoleconsole.log(store)


    return next()

})





export default router
